import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";



import Loading from "../components/Loading";

import BookmakerTable from "../components/BookmakerTable";
import TossTable from "../components/TossTable";
import FancyTable from "../components/FancyTable";
import BetsTable from "../components/BetsTable";
import MatchHeader from "../components/MatchHeader";
import { useGetEventSummaryQuery, useGetEventBetsQuery, useGetFancySessionSummaryQuery, useGetFanciesWithBetsQuery, useGetFancyDownlinePnlQuery } from "../services/sports/sportsApi";
import routes from "../config/routes";
import { getResolvedWsBaseUrl } from "../utils/domainConfig";


const MatchDetailPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const [oddsData, setOddsData] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const prevOddsRef = useRef({});
  const [bookFilter, setBookFilter] = useState("my");
  const [matchName, setMatchName] = useState("");
  const [betsPage, setBetsPage] = useState(1);

  const { data: eventSummary } = useGetEventSummaryQuery({
    eventId: matchId,
    filter: bookFilter
  }, {
    skip: !matchId,
    pollingInterval: 3000
  });

  const { data: eventBets, isLoading: betsLoading } = useGetEventBetsQuery({
    eventId: matchId,
    page: betsPage,
    limit: 50
  }, {
    skip: !matchId,
    pollingInterval: 5000
  });

  const { data: fancySessionSummary, isLoading: sessionLoading, isFetching: sessionFetching, error: sessionError, refetch: refetchSessionSummary } = useGetFancySessionSummaryQuery({
    eventId: matchId
  }, {
    skip: !matchId
  });

  const { data: fanciesWithBetsData } = useGetFanciesWithBetsQuery(matchId, {
    skip: !matchId
  });

  // Call fancy downline PnL API
  const { data: fancyDownlinePnl, isLoading: downlineLoading, isFetching: downlineFetching, error: downlineError, refetch: refetchDownlinePnl } = useGetFancyDownlinePnlQuery({
    eventId: matchId
  }, {
    skip: !matchId
  });

  const handleFilterChange = (filter) => {
    setBookFilter(filter);
  };

  useEffect(() => {
    let reconnectTimer = null;
    let isUnmounted = false;

    const connect = () => {
      if (isUnmounted) return;

      // Clear stale data on reconnect
      setOddsData(null);

      const ws = new WebSocket(`${getResolvedWsBaseUrl(import.meta.env.VITE_WS_URL)}/ws/admin`);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsConnected(true);
        ws.send(JSON.stringify({ type: 'subscribe', beventId: matchId }));
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.ename) setMatchName(data.ename);
        if (data.type === 'oddsData') setOddsData(data.data);
      };

      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
      };

      ws.onclose = () => {
        setWsConnected(false);
        if (isUnmounted) return;
        reconnectTimer = setTimeout(connect, 3000);
      };
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Tab came back — reconnect if connection is not open
        const ws = wsRef.current;
        if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
          clearTimeout(reconnectTimer);
          connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    connect();

    return () => {
      isUnmounted = true;
      clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', handleVisibility);
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'unsubscribe' }));
        ws.close();
      }
    };
  }, [matchId]);

  // Handle Show Bets button click
  const handleShowBets = (fancyId) => {
    navigate(routes.FANCYSESSIONDETAIL.replace(':matchId', matchId).replace(':fancyId', fancyId));
  };
  
  return (
    <div className="w-full bg-gray-100">

      {/* Reconnecting banner */}
      {!wsConnected && (
        <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-300 text-yellow-700 text-xs font-semibold px-4 py-2 mb-2 rounded">
          <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
          Reconnecting... please wait
        </div>
      )}

<div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
  <div className="w-full">
    <MatchHeader matchId={matchId} matchName={matchName}/>
    <BookmakerTable oddsData={oddsData} prevOddsRef={prevOddsRef} eventSummary={eventSummary} onFilterChange={handleFilterChange}/>
    <TossTable oddsData={oddsData} prevOddsRef={prevOddsRef} eventSummary={eventSummary} />
    <FancyTable
      oddsData={oddsData}
      eventSummary={fancySessionSummary}
      onFilterChange={handleFilterChange}
      beventId={matchId}
      fanciesWithBets={fanciesWithBetsData}
      eventBets={eventBets}
    />
  </div>
  <div className="w-full">
    <BetsTable eventBets={eventBets} isLoading={betsLoading} currentPage={betsPage} onPageChange={setBetsPage} />
  </div>
</div>
    
      <div className="mt-5">
        <div className="bg-white rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-[#1a3a40] text-white p-3 flex justify-between items-center">
            <span className="font-semibold">My Sessions P&L</span>
            <button
              onClick={refetchSessionSummary}
              disabled={sessionFetching}
              className="bg-blue-500 px-3 py-1 text-sm rounded hover:bg-blue-600 disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {sessionFetching && (
                <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              )}
              {sessionFetching ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {sessionLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loading />
              </div>
            ) : sessionError ? (
              <div className="p-4 text-red-500 text-center">
                {sessionError.message || "Failed to load session summary"}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-700 text-white">
                    <th className="p-2 border border-gray-600 text-left">Title</th>
                    <th className="p-2 border border-gray-600 text-left">P&L</th>
                    <th className="p-2 border border-gray-600 text-left">Won By</th>
                    <th className="p-2 border border-gray-600 text-left">Net P&L</th>
                    <th className="p-2 border border-gray-600 text-left">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {/* Total Row */}
                  <tr className="bg-gray-50">
                    <td className="p-2 border border-gray-300">Total</td>
                    <td className={`p-2 border border-gray-300 ${fancySessionSummary?.summary?.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fancySessionSummary?.summary?.pnl?.toFixed(2) || '0.00'}
                    </td>
                    <td className="p-2 border border-gray-300"></td>
                    <td className={`p-2 border border-gray-300 ${fancySessionSummary?.summary?.netPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fancySessionSummary?.summary?.netPL?.toFixed(2) || '0.00'}
                    </td>
                    <td className="p-2 border border-gray-300"></td>
                  </tr>
                  
                  {/* Data Rows */}
                  {fancySessionSummary?.data?.map((item, index) => (
                    <tr key={item.fancyId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="p-2 border border-gray-300">{item.fancyName}</td>
                      <td className={`p-2 border border-gray-300 ${item.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {item.pnl?.toFixed(2)}
                      </td>
                      <td className="p-2 border border-gray-300">{item.wonBy}</td>
                      <td className={`p-2 border border-gray-300 ${item.netPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {item.netPL?.toFixed(2)}
                      </td>
                      <td className="p-2 border border-gray-300">
                        <button 
                          onClick={() => handleShowBets(item.fancyId)}
                          className="bg-orange-500 text-white px-3 py-1 text-xs rounded hover:bg-orange-600"
                        >
                          Show Bets
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Downline Session P&L */}
      <div className="mt-5">
        <div className="bg-white rounded-lg overflow-hidden">
          <div className="bg-[#1a3a40] text-white p-3 flex justify-between items-center">
            <span className="font-semibold">Downline Session P&L</span>
            <button
              onClick={refetchDownlinePnl}
              disabled={downlineFetching}
              className="bg-blue-500 px-3 py-1 text-sm rounded hover:bg-blue-600 disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {downlineFetching && (
                <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              )}
              {downlineFetching ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          <div className="overflow-x-auto">
            {downlineLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loading />
              </div>
            ) : downlineError ? (
              <div className="p-4 text-red-500 text-center">
                {downlineError.message || "Failed to load downline P&L"}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-700 text-white">
                    <th className="p-2 border border-gray-600 text-left">Name</th>
                    <th className="p-2 border border-gray-600 text-left">Match Amt.</th>
                    <th className="p-2 border border-gray-600 text-left">Session Amt.</th>
                    <th className="p-2 border border-gray-600 text-left">Total P&L</th>
                    <th className="p-2 border border-gray-600 text-left">Match Comm</th>
                    <th className="p-2 border border-gray-600 text-left">Session Comm</th>
                    <th className="p-2 border border-gray-600 text-left">Total Comm</th>
                    <th className="p-2 border border-gray-600 text-left">Total Amount</th>
                    <th className="p-2 border border-gray-600 text-left">Down Share</th>
                    <th className="p-2 border border-gray-600 text-left">Balance</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {/* Total Row */}
                  <tr className="bg-gray-50">
                    <td className="p-2 border border-gray-300 font-semibold">Total</td>
                    {[
                      fancyDownlinePnl?.totals?.matchAmt,
                      fancyDownlinePnl?.totals?.sessAmt,
                      fancyDownlinePnl?.totals?.total,
                      fancyDownlinePnl?.totals?.matchComm,
                      fancyDownlinePnl?.totals?.sessComm,
                      fancyDownlinePnl?.totals?.totalComm,
                      fancyDownlinePnl?.totals?.totalAmount,
                      fancyDownlinePnl?.totals?.downlineShare,
                      fancyDownlinePnl?.totals?.netAmount,
                    ].map((val, i) => (
                      <td key={i} className={`p-2 border border-gray-300 ${(val ?? 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {(val ?? 0).toFixed(2)}
                      </td>
                    ))}
                  </tr>

                  {/* Data Rows */}
                  {fancyDownlinePnl?.data?.map((item, index) => (
                    <tr key={item.userId ?? index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="p-2 border border-gray-300">{item.name}</td>
                      {[item.matchAmt, item.sessAmt, item.total, item.matchComm, item.sessComm, item.totalComm, item.totalAmount, item.downlineShare, item.netAmount].map((val, i) => (
                        <td key={i} className={`p-2 border border-gray-300 ${(val ?? 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {(val ?? 0).toFixed(2)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchDetailPage;
